/**
 * Pairing managers to the team names history has lost.
 *
 * Yahoo hides manager names before 2009, so the archives hold 62 team names with nobody behind
 * them — all of 2003-2008, plus 'roid rage in 2009 and 2010. Every records board in the app
 * refuses to count those seasons, and says so. This is the screen that fixes it.
 *
 * The design principle is that this is a MEMORY EXERCISE, not data entry. Nobody is going to
 * work through 62 rows of a bare dropdown. So:
 *
 *   The engine does the certain part. Two independent witnesses are already in the data: a joke
 *   carried between seasons ("Samanticiders" runs unresolved 2006-2008 and resolves to SAMANT in
 *   2009), and the Draftboard workbook's draft orders, which name some managers outright and so
 *   fix the candidate SET for the teams they leave unnamed. Where those two agree, or where
 *   elimination leaves one answer, it is offered as a single button.
 *
 *   The rest is ranked, not blank, and every suggestion shows its reasoning on the row rather
 *   than in a tooltip — a percentage nobody can check is just a guess wearing a number.
 *
 *   Context is on screen. The teams Yahoo DID name that season are listed beside the open ones,
 *   because "who's left?" is how a person actually solves this.
 *
 * Saving a pairing widens Records immediately: careers, head-to-head, the podium and every
 * leaderboard recompute against the overlay, and the coverage note under each board moves with
 * them. Withdrawing one narrows them again. Nothing derived is written down.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  FRANCHISES, RECORDED_SOURCE, confidentPairings, isRecorded, knownGms, managerName,
  pairingBoard, pairingConflicts, pairingProgress, type OpenTeam, managerShort,
} from '../domain/identity.js';
import { namedSeasons, ordinal, teamRecord } from '../domain/records.js';
import { GmBadge } from './GmBadge.js';
import { GateTag } from './Gate.js';
import { ManagerRoster } from './ManagerRoster.js';
import { AccountLinks } from './AccountLinks.js';
import { asFirestore, asMemoryStore } from '../store/provider.js';
import { usePairingVersion, useRosterVersion } from './pairings.js';
import { atLeast, type LeagueStore, type Role, type TeamPairing as Pairing } from '../store/types.js';
import { note } from './auditor.js';

const SEASON = 2026;

export function TeamPairing({ gm, role, store }: { gm: string; role: Role; store: LeagueStore }) {
  const version = usePairingVersion();
  // The suggestion engine and the picker both read the roster, so a manager added below has to
  // recompute them — not just the pairings.
  const roster = useRosterVersion();
  const v = `${version}.${roster}`;
  /**
   * ADMIN, not commissioner.
   *
   * A wrong pairing does not break anything visibly — it quietly rewrites twenty years of career
   * records, and the person reading them has no way to tell. Everybody can read this page; one
   * man changes it.
   */
  const canEdit = atLeast(role, 'admin');
  const [saved, setSaved] = useState<Pairing[]>([]);
  const [flash, setFlash] = useState<string | null>(null);
  /**
   * What is still open is open. Five team names across three seasons fits on one screen, and
   * making somebody click three times to find out what is left was the old wall in miniature.
   * Settled seasons stay shut and share a single accordion, because opening one is a spot-check
   * rather than a task.
   */
  const [shut, setShut] = useState<ReadonlySet<number>>(new Set());
  const [reviewing, setReviewing] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    const load = () => void store.teamPairings().then((r) => { if (live) setSaved(r); });
    load();
    const off = store.subscribe(SEASON, (w) => { if (w === 'teamPairings') load(); });
    return () => { live = false; off(); };
  }, [store]);

  const board = useMemo(pairingBoard, [v]);
  const progress = useMemo(pairingProgress, [v]);
  const confident = useMemo(confidentPairings, [v]);
  const conflicts = useMemo(pairingConflicts, [v]);
  const unlocked = useMemo(() => namedSeasons(), [v]);
  const gms = useMemo(knownGms, [v]);

  const noteFor = (season: number, team: string) =>
    saved.find((p) => p.season === season && p.team === team);

  const say = (msg: string) => {
    setFlash(msg);
    setTimeout(() => setFlash(null), 4000);
  };

  async function assign(season: number, team: string, to: string) {
    if (!to) {
      note(store, gm, {
        kind: 'delete', what: `Unpaired “${team}” (${season})`,
        target: `teamPairings/${season}-${team}`,
      });
      await store.deleteTeamPairing(season, team);
      say(`Withdrew ${team} (${season})`);
      return;
    }
    await store.saveTeamPairing({
      season, team, gm: to, pairedBy: gm, pairedAt: new Date().toISOString(),
    });
    say(`${season} ${team} → ${to}`);
  }

  async function acceptAll() {
    for (const c of confident) {
      await store.saveTeamPairing({
        season: c.season, team: c.team, gm: c.gm,
        pairedBy: gm, pairedAt: new Date().toISOString(),
        note: `Accepted in bulk — ${c.why}`,
      });
    }
    say(`Filled in ${confident.length} team${confident.length === 1 ? '' : 's'}. Each one shows its reasoning on the season below.`);
  }

  /**
   * The same man under the same team name across six seasons is ONE fact, not six. Listing it six
   * times with the same sentence under each buries the two entries that are actually different.
   */
  const grouped = useMemo(() => {
    const by = new Map<string, { team: string; gm: string; seasons: number[]; why: string }>();
    for (const c of confident) {
      const key = `${c.team}|${c.gm}`;
      const hit = by.get(key);
      if (hit) hit.seasons.push(c.season);
      else by.set(key, { team: c.team, gm: c.gm, seasons: [c.season], why: reason(c.why) });
    }
    return [...by.values()].sort((a, b) => b.seasons.length - a.seasons.length
      || a.team.localeCompare(b.team));
  }, [confident]);

  const total = progress.open + progress.done;
  const pct = total ? Math.round((progress.done / total) * 100) : 100;
  const done = progress.open === 0;

  // Three seasons are still short and five are finished. Rendering eight identical cards buries
  // the work in the archive, so the open ones come first and open themselves, and the settled
  // ones fold into a single strip that can still be opened to check or override an answer.
  const openSeasons = board.filter((s) => !s.complete);
  const settled = board.filter((s) => s.complete);

  return (
    <div className="app wide">
      <header className="pagehead">
        <div>
          <h1>Manager history</h1>
          <p className="sub">
            {done ? (
              <>All {total} team names from before 2009 have a manager behind them.</>
            ) : (
              <>
                {progress.done} of {total} pre-2009 team names paired.{' '}
                <b>{progress.open}</b> across {progress.seasons.length} season
                {progress.seasons.length === 1 ? '' : 's'} still open.
              </>
            )}
          </p>
        </div>
        <div className="pairstat">
          <div className="v">{progress.done}<span className="mut">/{total}</span></div>
          <div className="mut">paired</div>
          <div className="bar" aria-hidden="true"><span style={{ width: `${pct}%` }} /></div>
          {/* This is where somebody spends an hour on memory. Say whether it is being kept. */}
          <SaveState store={store} />
        </div>
      </header>

      {/* Everybody reads this page; one man edits it. So it opens on the league rather than on
          the work — who has ever played, and how the ten seats passed between them — and the
          handful of team names still missing a manager comes after. */}
      <ManagerRoster actor={gm} role={role} store={store} />

      {/* Signing in is a different question from who has ever played, but it is the same question
          about identity, so it lives beside the roster rather than on a screen of its own. */}
      <AccountLinks role={role} />

      <section className="card franchises">
        <h3 className="striphead">How the ten seats have changed hands</h3>
        <p className="note">
          Two of the current managers inherited a roster rather than starting one, which is why a
          career table splits where a franchise does not. Each handover below is checked against
          keeper continuity — a manager cannot keep players he never had.
        </p>
        <ul className="franchiselist">
          {FRANCHISES.map((f) => (
            <li key={f.id}>
              <div className="chain">
                {f.chain.map((link, i) => (
                  <span className="link" key={link.gm}>
                    {i > 0 && <span className="arrow" aria-hidden="true">→</span>}
                    <GmBadge gm={link.gm} />
                    <b>{managerName(link.gm)}{link.with ? ` & ${managerName(link.with)}` : ''}</b>
                    <span className="mut">
                      {link.to === link.from ? link.from : `${link.from}–${link.to ?? 'now'}`}
                    </span>
                  </span>
                ))}
              </div>
              <p className="mut why">{f.evidence}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className={`card pairsummary ${canEdit ? 'gated admin' : ''}`}>
        {canEdit && <GateTag level="admin" what="Admin only — a wrong pairing rewrites the records" />}
        <div>
          <h3 className="striphead">
            {done
              ? 'Every team has a name'
              : canEdit
                ? `${progress.open} left to pair`
                : `${progress.open} teams nobody can name`}
          </h3>
          {done ? (
            <p className="note">
              All {unlocked.length} seasons, {Math.min(...unlocked)}–{Math.max(...unlocked)}, are
              in every records board.
            </p>
          ) : (
            <>
              <ul className="tight leftlist">
                {openSeasons.map((s) => {
                  const gaps = s.open.filter((o) => !o.assigned);
                  return (
                    <li key={s.season}>
                      <b>{s.season}</b>
                      <span className="mut"> — {gaps.length} to go: </span>
                      {gaps.map((o, i) => {
                        const r = teamRecord(o.season, o.team);
                        return (
                          <span key={o.key}>
                            {i > 0 && ', '}
                            <span className="tm">{o.team}</span>
                            {r && (
                              <span className="mut">
                                {' '}({r.w}-{r.l}, {ordinal(r.rank)})
                              </span>
                            )}
                          </span>
                        );
                      })}
                    </li>
                  );
                })}
              </ul>
              <p className="note">
                {canEdit
                  ? `Counting ${unlocked.length} seasons so far, ${Math.min(...unlocked)}–${Math.max(...unlocked)}. `
                  : `Records count ${unlocked.length} seasons, ${Math.min(...unlocked)}–${Math.max(...unlocked)}. `}
                Each of these is a season the boards cannot count — if you remember one, tell the
                commissioner.
              </p>
            </>
          )}
        </div>
        {flash && <p className="chip good flashmsg">{flash}</p>}
      </section>

      {/*
        A bulk action whose contents you cannot see is a bulk action nobody should press. What
        this used to be — a button reading "Accept 9 certain pairings" — asked for trust in a
        number. It now shows its working: every proposed answer, the reasoning behind it, and a
        way to take them one at a time. It is empty once everything derivable has been taken.
      */}
      {confident.length > 0 && (
        <section className={`card worked ${canEdit ? 'gated admin' : ''}`}>
          {canEdit && <GateTag level="admin" />}
          <div className="striptop">
            <h3 className="striphead">
              {grouped.length} name{grouped.length === 1 ? '' : 's'} the app can work out
            </h3>
            <span className="mut">
              {confident.length} team-season{confident.length === 1 ? '' : 's'} in all
            </span>
          </div>
          <p className="note">
            These are not guesses. Each one either matches a team name the manager used again in a
            season Yahoo <em>did</em> record, or is the only manager left once the draft archive has
            accounted for everybody else that year — usually both. Read them, then take the lot or
            one at a time.
          </p>
          <ul className="workedlist">
            {grouped.map((g) => (
              <li key={`${g.team}|${g.gm}`}>
                <span className="tm">{g.team}</span>
                <span className="arrow" aria-hidden="true">→</span>
                <GmBadge gm={g.gm} withName />
                <span className="yrs">
                  {g.seasons.length === 1 ? 'in ' : ''}
                  {g.seasons.slice().sort((a, b) => a - b).join(', ')}
                </span>
                {canEdit && (
                  <button type="button" className="linkbtn tiny take"
                    onClick={() => void Promise.all(g.seasons.map((y) => assign(y, g.team, g.gm)))}>
                    accept {g.seasons.length > 1 ? `all ${g.seasons.length}` : ''}
                  </button>
                )}
                <span className="mut why">{g.why}</span>
              </li>
            ))}
          </ul>
          {canEdit && (
            <div className="actions">
              <span className="declstatus">
                Nothing is permanent — any of these can be changed on the season below.
              </span>
              <button type="button" className="btn primary" onClick={() => void acceptAll()}>
                Accept all {confident.length}
              </button>
            </div>
          )}
        </section>
      )}

      {conflicts.length > 0 && (
        <section className="card banner">
          <h3 className="striphead bannerhead">Two teams, one manager</h3>
          <ul className="tight">
            {conflicts.map((c) => (
              <li key={`${c.season}${c.gm}`}>
                <b>{c.season}</b> — {managerShort(c.gm)} is on both {c.teams.map((t) => `"${t}"`).join(' and ')}.
                One of them is wrong; the season stays closed until it is fixed.
              </li>
            ))}
          </ul>
        </section>
      )}



      {openSeasons.map((s) => (
        <Season
          key={s.season}
          s={s}
          gms={gms}
          canEdit={canEdit}
          open={!shut.has(s.season)}
          onToggle={() => setShut((was) => {
            const next = new Set(was);
            if (next.has(s.season)) next.delete(s.season);
            else next.add(s.season);
            return next;
          })}
          noteFor={noteFor}
          onAssign={assign}
        />
      ))}

      {settled.length > 0 && (
        <section className="card settledwrap">
          <div className="striptop">
            <h3 className="striphead">Who held which team, season by season</h3>
            <span className="mut">
              {settled.length} season{settled.length === 1 ? '' : 's'} · every team named
            </span>
          </div>
          <p className="note">
            The years Yahoo forgot, reconstructed. Open one to see who was playing under what
            name{canEdit ? ' — and to change an answer if it is wrong' : ''}.
          </p>
          {settled.map((s) => (
            <Season
              key={s.season}
              s={s}
              gms={gms}
              canEdit={canEdit}
              open={reviewing === s.season}
              onToggle={() => setReviewing(reviewing === s.season ? null : s.season)}
              noteFor={noteFor}
              onAssign={assign}
              inline
            />
          ))}
        </section>
      )}


      <p className="note src">{RECORDED_SOURCE}</p>
    </div>
  );
}

/**
 * One season.
 *
 * The header answers "how far off is this" without opening it, and once opened the FIRST thing on
 * screen is who already holds a team that year — because "who is left?" is how a person actually
 * solves this, and before 2009 Yahoo names nobody, so that list has to be built from the pairings
 * themselves rather than read off the archive.
 */
function Season({ s, gms, canEdit, open, onToggle, noteFor, onAssign, inline = false }: {
  s: ReturnType<typeof pairingBoard>[number];
  gms: string[];
  canEdit: boolean;
  open: boolean;
  onToggle: () => void;
  noteFor: (season: number, team: string) => Pairing | undefined;
  onAssign: (season: number, team: string, to: string) => Promise<void>;
  inline?: boolean;
}) {
  // In a season still short a name, the rows already answered are noise — nine solved rows above
  // the one unsolved one is how 2006 read. They are summarised in the strip below and available
  // on request; a settled season shows everything, because reviewing is the only reason to open it.
  const [showDone, setShowDone] = useState(false);
  const gaps = s.open.filter((o) => !o.assigned);
  const placed = [
    ...s.known.map((k) => ({ gm: k.gm, team: k.team })),
    ...s.open.filter((o) => o.assigned).map((o) => ({ gm: o.assigned!, team: o.team })),
  ].sort((a, b) => managerShort(a.gm).localeCompare(managerShort(b.gm)));

  return (
    <section
      className={`${inline ? 'pairseason inline' : 'card pairseason'} ${s.complete ? 'settled' : ''}`}
    >
      <button type="button" className="striptop pairtoggle" aria-expanded={open} onClick={onToggle}>
        <h3 className="striphead">{s.season}</h3>
        <span className={`chip ${s.complete ? 'good' : 'warning'}`}>
          {s.complete ? 'complete' : `${gaps.length} to go`}
        </span>
        <span className="mut">
          {s.complete
            ? `all ${s.open.length + s.known.length} teams named`
            : gaps.map((o) => o.team).join(' · ')}
        </span>
        <span className="mut caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <>
          {placed.length > 0 && (
            <p className="knownrow">
              <span className="mut">
                {s.known.length > 0 ? 'Already placed:' : 'Already placed (all from memory):'}
              </span>
              {placed.map((k) => (
                <span className="knowngm" key={`${k.gm}${k.team}`}>
                  <GmBadge gm={k.gm} withName /> <span className="mut">{k.team}</span>
                </span>
              ))}
            </p>
          )}
          <ul className="pairlist">
            {(s.complete || showDone ? s.open : gaps).map((o) => (
              <Row
                key={o.key}
                team={o}
                gms={gms}
                canEdit={canEdit}
                note={noteFor(o.season, o.team)}
                onAssign={(to) => void onAssign(o.season, o.team, to)}
              />
            ))}
          </ul>
          {!s.complete && s.open.length > gaps.length && (
            <button type="button" className="linkbtn tiny showdone"
              onClick={() => setShowDone(!showDone)}>
              {showDone
                ? 'hide the names already recorded'
                : `show the ${s.open.length - gaps.length} already recorded`}
            </button>
          )}
        </>
      )}
    </section>
  );
}

/**
 * Trim the engine's reasoning for a grouped row. It names the season's other unlisted managers,
 * which is worth having on a single-season row and is repetition once six seasons agree.
 */
function reason(why: string): string {
  return why
    .replace(/\s*\([A-Z, ]+\)/g, '')
    .replace(/the \d{4} draft archive/g, 'the draft archive')
    .replace(/in \d{4}$/, 'in a season Yahoo did record');
}

/**
 * One line: where is this work going?
 *
 * Worth saying on this screen specifically. Pairing pre-2009 teams is an hour of somebody digging
 * through memory and old Yahoo pages, and the difference between "this reaches the league" and
 * "this is in one browser and will be gone if you clear your cache" is the difference between
 * doing it once and doing it twice.
 */
function SaveState({ store }: { store: LeagueStore }) {
  const fs = asFirestore(store);
  if (fs) {
    return fs.status.error
      ? <div className="mut savetag bad">the league database refused a read</div>
      : <div className="mut savetag ok">saved to the league</div>;
  }
  const mem = asMemoryStore(store);
  if (!mem) return null;
  return mem.persistence.available
    ? <div className="mut savetag ok">saved in this browser</div>
    : <div className="mut savetag bad">not being saved — back it up</div>;
}

function Row({ team, gms, canEdit, note, onAssign }: {
  team: OpenTeam;
  gms: string[];
  canEdit: boolean;
  note: Pairing | undefined;
  onAssign: (gm: string) => void;
}) {
  const top = team.suggestions[0];
  /**
   * What this team actually did that season.
   *
   * "THE GREEN LANTERN" means nothing to anybody twenty years on. "8-6, seventh of ten" is a
   * season somebody remembers having, and it is the only lead the app can offer on the five names
   * the archives cannot reach.
   */
  const rec = useMemo(() => teamRecord(team.season, team.team), [team.season, team.team]);
  // Shipped with the build rather than saved by a commissioner — worth saying, because it means
  // the answer came out of somebody's memory rather than out of the archives.
  const recorded = isRecorded(team.season, team.team);
  return (
    <li className={`pairrow ${team.assigned ? 'on' : ''}`}>
      <div className="pairname">
        <b>{team.team}</b>
        {rec ? (
          <span className="mut season">
            {rec.w}-{rec.l}{rec.t ? `-${rec.t}` : ''} · {ordinal(rec.rank)} of {rec.of}
            {/* Third place is already the rank; saying it twice reads as two facts. */}
            {rec.finish === 'champion' && <b className="won"> · won it</b>}
            {rec.finish === 'runner-up' && ' · lost the final'}
            {rec.finish !== 'champion' && rec.finish !== 'runner-up'
              && (rec.playoff ? ' · made the playoffs' : ' · missed')}
          </span>
        ) : <span className="mut">team {team.teamId}</span>}
      </div>

      <div className="pairwho">
        {team.assigned ? <GmBadge gm={team.assigned} withName /> : <span className="mut">unknown</span>}
        {note?.pairedBy && <span className="mut small"> · {managerShort(note.pairedBy)}</span>}
        {!note && recorded && <span className="chip good tiny">recorded</span>}
      </div>

      {canEdit ? (
        <div className="pairpick">
          <select
            value={team.assigned ?? ''}
            aria-label={`Manager for ${team.team} in ${team.season}`}
            onChange={(e) => onAssign(e.target.value)}
          >
            <option value="">— unknown —</option>
            {gms.map((g) => <option key={g} value={g}>{managerName(g)}</option>)}
          </select>
        </div>
      ) : <div className="pairpick" />}

      <div className="pairsug">
        {team.suggestions.length === 0 && (
          <span className="mut small">
            nothing in the archives points anywhere — this one is memory
          </span>
        )}
        {team.suggestions.map((sg) => (
          <button
            key={sg.gm}
            type="button"
            className={`chip sug ${sg.confidence >= 1 ? 'strong' : ''}`}
            disabled={!canEdit || team.assigned === sg.gm}
            title={sg.why}
            onClick={() => onAssign(sg.gm)}
          >
            {managerShort(sg.gm)}
            <span className="mut">{Math.round(sg.confidence * 100)}%</span>
          </button>
        ))}
      </div>

      {/* The reasoning, in the open. A suggestion nobody can check is just a guess with a
          percentage on it — and on a phone there is no hover to reveal a title attribute. */}
      {top && !team.assigned && (
        <p className="pairwhy mut">
          {managerName(top.gm)}: {top.why}
        </p>
      )}
      {team.assigned && note?.note && <p className="pairwhy mut">{note.note}</p>}
      {team.assigned && !note && recorded && (
        <p className="pairwhy mut">
          {managerName(team.assigned)} — recorded with the build from the league&rsquo;s own
          memory, not derived from the archives. Overriding it here replaces it.
        </p>
      )}
    </li>
  );
}
